import { useEffect } from 'react'

//utils
import {
  connect /*,  InferableComponentEnhancerWithProps  */,
} from 'react-redux'
import { Redirect, Route, Switch, withRouter } from 'react-router'
import { createStructuredSelector } from 'reselect'

//constants
import { IUserInfo, UserSections } from '../models/User'
import { ROUTES } from './../module/constants'

//components
import ErrorModal from '../components/Modals/ErrorModal/ErrorModal'
import Footer from './../components/Footer/Footer'
import Header from './../components/Header/Header'
import TimeExpiredModal from './../components/Modals/TimeExpiredModal/TimeExpiredModal'
import Sidebar from './../components/Sidebar/Sidebar'
import MainContentContainer from './../containers/MainContentContainer/MainContentContainer'
import SidebarBackdrop from './../ui/SidebarBackdrop/SidebarBackdrop'

//actions & selectors
import {
  changeLanguage,
  loggedUserRequest,
  setTriggerErrorModal,
} from './module/actions'
import {
  selectAccessFromInternet,
  selectErrorCode,
  selectErrorText,
  selectModalTrigger,
  selectUserInfo,
} from './module/selectors'

//pages
import ApplicationsAbilitationsManagement from '../pages/AdminConsole/ApplicationsAbilitationsManagement/Loadable'
import AuditManagement from '../pages/AdminConsole/AuditManagement/Loadable'
import adminBRManagement from '../pages/AdminConsole/BRManagement/Loadable'
import CatalogManagement from '../pages/AdminConsole/CatalogManagement/Loadable'
import CertificationConfigurationsManagement from '../pages/AdminConsole/CertificationConfigurationsManagement/Loadable'
import ExternalPortalManagement from '../pages/AdminConsole/ExternalPortalManagement/Loadable'
import ManualsManagement from '../pages/AdminConsole/ManualsManagement/Loadable'
import NotifyManagement from '../pages/AdminConsole/NotifyAdminManagement/NotifyManagement'
import PasswordManagement from '../pages/AdminConsole/PasswordManagement/Loadable'
import ProfilesManagement from '../pages/AdminConsole/ProfilesManagement/Loadable'
import RecoveryPointManagement from '../pages/AdminConsole/RecoveryPointManagement/Loadable'
import ReportManagement from '../pages/AdminConsole/ReportManagement/Loadable'
import ServiceRequestManagement from '../pages/AdminConsole/ServiceRequestManagement/Loadable'
import SODManagement from '../pages/AdminConsole/SODManagement/Loadable'
import TechnicalUser from '../pages/TechnicalUser/Loadable'
import TablesConfigurationManagement from '../pages/AdminConsole/TablesConfigurationManagement/Loadable'
import TemplateMailManagement from '../pages/AdminConsole/TemplateMailManagement/Loadable'
import TemplateSmsManagement from '../pages/AdminConsole/TemplateSmsManagement/Loadable'
import TemporaryAbilitationsManagement from '../pages/AdminConsole/TemporaryAbilitationsManagement/Loadable'
import UsersManagement from '../pages/AdminConsole/UsersManagement/Loadable'
import AdminWelcome from '../pages/AdminConsole/Welcome/AdminWelcome'
import WorkflowManagement from '../pages/AdminConsole/WorkflowManagement/Loadable'
import BusinessRolesManagement from '../pages/BusinessRolesManagement/Loadable'
import Catalog from '../pages/CatalogManagement/Loadable'
import CertificationReference from '../pages/CertificationReference/Loadable'
import ChangePassword from '../pages/ChangePassword/Loadable'
import CreateCertification from '../pages/CreateCertifaction/Loadable'
import DelegationManagement from '../pages/DelegationManagement'
import EmergencyUsers from '../pages/EmergencyUsers/Loadable'
import EntitlementReference from '../pages/EntitlementReference/Loadable'
import ErrorPage from '../pages/ErrorPage/Loadable'
import ExternalPortal from '../pages/ExternalPortal/Loadable'
import HistoryRequest from '../pages/HistoryRequest/Loadable'
import ManualsReference from '../pages/ManualsReference/Loadable'
import ProfileManagement from '../pages/ProfileManagement/Loadable'
import Report from '../pages/Report'
import ResetPassword from '../pages/ResetPassword/Loadable'
import ServiceRequest from '../pages/ServiceRequest/Loadable'
import TasksManagement from '../pages/TaskManagement'
import TemporaryAbilitations from '../pages/TemporaryAbilitations/Loadable'
import UsersReference from '../pages/UsersReference'
import AbilitationManagement from './../pages/AbilitationManagement/Loadable'
import ApprovalsManagement from './../pages/ApprovalsManagement/Loadable'
import Welcome from './../pages/Welcome/Welcome'
import UigpManagement from '../pages/AdminConsole/UigpManagement/Loadable'
import TechnicalUsersManagement from '../pages/AdminConsole/TechnicalUsersManagement/Loadable'
import UserEntitlements from '../pages/UserEntitlements/UserEntitlements'
import CsvManagement from '../pages/AdminConsole/CsvUploaderManagement/Loadable'

//props
interface IAppProps {
  loggedUserRequest: any
  userInfo: IUserInfo
  modalTrigger: boolean
  errorCode: number
  errorText: string
  isAccessFromInternet: boolean
  setTriggerConfirmModal: (val: boolean) => {}
  changeLanguage: (lang: string) => void
}

const Main = (props: IAppProps) => {
  const {
    loggedUserRequest,
    userInfo,
    modalTrigger,
    errorCode,
    errorText,
    isAccessFromInternet,
    setTriggerConfirmModal,
  } = props

  useEffect(() => {
    loggedUserRequest()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <Header />
      <SidebarBackdrop />
      <Sidebar />
      <TimeExpiredModal />
      <ErrorModal
        hasError={modalTrigger}
        errorCode={errorCode}
        errorText={errorText}
        unSetError={() => setTriggerConfirmModal(false)}
      />
      <MainContentContainer>
        {userInfo && (
          <Switch>
            <Route path={ROUTES.WELCOME} component={Welcome} />
            <Redirect
              strict
              exact
              path={'/'}
              to={{ pathname: ROUTES.WELCOME }}
            />

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.HISTORY_REQUEST) && (
                <Route
                  path={ROUTES.REQUESTHISTORY}
                  component={HistoryRequest}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.MANUALS) && (
                <Route path={ROUTES.MANUALS} component={ManualsReference} />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.EXTERNAL_PORTAL) && (
                <Route
                  path={ROUTES.EXTERNAL_PORTAL}
                  component={ExternalPortal}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.APPROVALS) && (
                <Route
                  path={ROUTES.APPROVALS}
                  component={ApprovalsManagement}
                />
              )}

            {!isAccessFromInternet &&
              userInfo.sections &&
              userInfo.sections.includes(UserSections.TASK) && (
                <Route path={ROUTES.TASKS} component={TasksManagement} />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(
                UserSections.USER_ENTITLEMENT_REQUEST
              ) && (
                <Route
                  path={ROUTES.ABILITATIONREQUEST}
                  component={AbilitationManagement}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(
                UserSections.TEMPORARY_ABILITATIONS
              ) && (
                <Route
                  path={ROUTES.TEMPORARY_ABILITATIONS}
                  component={TemporaryAbilitations}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.CATALOG) && (
                <Route path={ROUTES.CATALOG} component={Catalog} />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.DELEGATIONS) && (
                <Route
                  path={ROUTES.DELEGATES}
                  component={DelegationManagement}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.EXAMINE_USERS) && (
                <Route
                  path={ROUTES.USERSREFERENCE}
                  component={UsersReference}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.EXAMINE_ENTITLEMENTS) && (
                <Route
                  path={ROUTES.ENTITLEMENT_REFERENCE}
                  component={EntitlementReference}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.USER_ENTITLEMENTS) && (
                <Route
                  path={ROUTES.USER_ENTITLEMENT}
                  component={UserEntitlements}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.RACF_USER) && (
                <Route
                  path={ROUTES.EMERGENCY_USERS}
                  component={EmergencyUsers}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.BUSINESS_ROLE) && (
                <Route
                  path={ROUTES.BR_MANAGEMENT}
                  component={BusinessRolesManagement}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.PROFILE_MGMT) && (
                <Route
                  path={ROUTES.PROFILE_MGMT}
                  component={ProfileManagement}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.RESET_PWD) && (
                <Route path={ROUTES.RESET_PASSWORD} component={ResetPassword} />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.CHANGE_PWD) && (
                <Route
                  path={ROUTES.CHANGE_PASSWORD}
                  component={ChangePassword}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.SERVICE_REQUEST) && (
                <Route
                  path={ROUTES.SERVICE_REQUEST}
                  component={ServiceRequest}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.REPORTS) && (
                <Route path={ROUTES.REPORT} component={Report} />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.EXAMINE_CAMPAIGN) && (
                <Route
                  path={ROUTES.CERTIFICATION_REFERENCE}
                  component={CertificationReference}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.TECHNICAL_USERS) && (
                <Route
                  path={ROUTES.TECHNICAL_USERS}
                  component={TechnicalUser}
                />
              )}

            {userInfo.sections &&
              userInfo.sections.includes(UserSections.CREATE_CAMPAIGN) && (
                <Route
                  path={ROUTES.CREATE_CERTIFICATION}
                  component={CreateCertification}
                />
              )}

            {!isAccessFromInternet &&
              userInfo.sections &&
              userInfo.sections.includes(UserSections.CONSOLE_ADMIN) && (
                <>
                  <Route path={ROUTES.CONSOLE_ADMIN} component={AdminWelcome} />
                  {userInfo.sections.includes(UserSections.ADM_USER_MGMT) && (
                    <Route
                      path={ROUTES.ADMIN_USERS_MGMT}
                      component={UsersManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_APP_ENT_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_APPLICATIONS_ABILITATIONS_MGMT}
                      component={ApplicationsAbilitationsManagement}
                    />
                  )}
                  {/* SET CORRECT CONE VISIBILITY */}
                  {userInfo.sections.includes(
                    UserSections.ADM_APP_ENT_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_BR_MGMT}
                      component={adminBRManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_CATALOG_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_CATALOG_MGMT}
                      component={CatalogManagement}
                    />
                  )}
                  {userInfo.sections.includes(UserSections.ADM_PWD_MGMT) && (
                    <Route
                      path={ROUTES.ADMIN_PWD_MGMT}
                      component={PasswordManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_EXTERNAL_PORTAL_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_EXTERNAL_PORTAL_MGMT}
                      component={ExternalPortalManagement}
                    />
                  )}
                  {userInfo.sections.includes(UserSections.ADM_SOD_MGMT) && (
                    <Route
                      path={ROUTES.ADMIN_SOD_MGMT}
                      component={SODManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_SERVICE_REQ_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_SERVICE_REQUEST_MGMT}
                      component={ServiceRequestManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_MANUALS_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_MANUALS_MGMT}
                      component={ManualsManagement}
                    />
                  )}
                  {userInfo.sections.includes(UserSections.ADM_REPORT_MGMT) && (
                    <Route
                      path={ROUTES.ADMIN_REPORT_MGMT}
                      component={ReportManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_TEMPLATE_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_TEMPLATE_MAIL_MGMT}
                      component={TemplateMailManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_SMS_TEMPLATE_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_TEMPLATE_SMS_MGMT}
                      component={TemplateSmsManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_WORKFLOW_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_WORKFLOW_MGMT}
                      component={WorkflowManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_TABLE_CONFIG_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_TABLES_CONFIGURATION_MGMT}
                      component={TablesConfigurationManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_RECOVERY_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_RECOVERY_POINT_MGMT}
                      component={RecoveryPointManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_PROFILES_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_PROFILES_MGMT}
                      component={ProfilesManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_RECERTIFICATION_CONFIGURATION
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_CERTIFICATION_CONFIGURATIONS_MGMT}
                      component={CertificationConfigurationsManagement}
                    />
                  )}
                  {userInfo.sections.includes(UserSections.ADM_AUDIT_MGMT) && (
                    <Route
                      path={ROUTES.ADMIN_AUDIT_MGMT}
                      component={AuditManagement}
                    />
                  )}
                  {userInfo.sections.includes(UserSections.ADM_NOTIFY_MGMT) && (
                    <Route
                      path={ROUTES.ADMIN_NOTIFY_MGMT}
                      component={NotifyManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_TEMPORARY_ABILITATIONS_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_TEMPORARY_ABILITATIONS_MGMT}
                      component={TemporaryAbilitationsManagement}
                    />
                  )}
                  {userInfo.sections.includes(UserSections.ADM_FILE_MGMT) && (
                    <Route
                      path={ROUTES.ADMIN_CSV_UPLOADER}
                      component={CsvManagement}
                    />
                  )}
                  {userInfo.sections.includes(UserSections.ADM_UIGP_MGMT) && (
                    <Route
                      path={ROUTES.ADMIN_UIGP_MGMT}
                      component={UigpManagement}
                    />
                  )}
                  {userInfo.sections.includes(
                    UserSections.ADM_TECHNICAL_USERS_MGMT
                  ) && (
                    <Route
                      path={ROUTES.ADMIN_TECHNICAL_USERS_MGMT}
                      component={TechnicalUsersManagement}
                    />
                  )}
                </>
              )}

            <Route path={ROUTES.FORBIDDEN} component={ErrorPage} />
            <Redirect path={'*'} to={{ pathname: ROUTES.FORBIDDEN }} />
          </Switch>
        )}
      </MainContentContainer>
      <Footer />
    </div>
  )
}

const mapStateToProps: any = () =>
  createStructuredSelector({
    userInfo: selectUserInfo,
    modalTrigger: selectModalTrigger,
    errorCode: selectErrorCode,
    errorText: selectErrorText,
    isAccessFromInternet: selectAccessFromInternet,
  })

const mapDispatchToProps: any = {
  setTriggerConfirmModal: (val: boolean) => setTriggerErrorModal(val),
  loggedUserRequest: () => loggedUserRequest(),
  changeLanguage,
}

const withConnect: any = connect(mapStateToProps, mapDispatchToProps)

export default withRouter(withConnect(Main))
